import { isLocationFileType, requireImageSubjectData } from "./image-subject";
import { rollDice } from "./dice";
import { advanceStoryClock, readStoryCalendar } from './calendar';
import { projectWorldRead, boundedReadPage } from './read-projection';
import { projectComputedFields } from "./computed-fields";
import { releaseReferenceIssues } from "./release-integrity";
import { structuredFieldCoverage, fieldDefinitionSchema, schemaField, type StructuredSchema } from "./structured-schema";
import { validateToolInvocation, type AgentRole, type ToolContract, type ToolInvocation } from "./agents";
import { playerVisibleDocuments } from "./presentation";
import { retrieveWorldDocuments } from "./context";
import type { WorldFileTypeRegistry } from "./file-types";
import { validateMapFileBindings, createEmptyStoryMap, executeMapBatch, moveMapToken, normalizeStoryMap, placeMapToken, removeMapToken, searchStoryMap, type MapOperation } from "./maps";
import type { JsonObject, JsonValue, WorldDocument, WorldTransaction } from "./types";
import { findDocumentByVfsPath, formatDocumentForVfs, inferKindFromVfsPath, vfsGrep, vfsListDirectory } from "./vfs";
import { applyWorldTransaction, computeBacklinks } from "./world";

export interface ToolExecutionContext {
  storyId: string;
  runId: string;
  agentRole: AgentRole;
  approved: boolean;
  now: string;
  expectedRevision?: number;
  invocationId?: string;
}

export interface ToolExecutionResult {
  ok: boolean;
  status: "completed" | "denied" | "failed";
  result?: JsonValue;
  error?: string;
  changedDocumentIds: string[];
}

export interface RegisteredTool {
  contract: ToolContract;
  execute: (arguments_: JsonObject, context: ToolExecutionContext) => Promise<ToolExecutionResult> | ToolExecutionResult;
}

export class ToolRegistry {
  readonly #tools = new Map<string, RegisteredTool>();

  register(tool: RegisteredTool): this {
    if (this.#tools.has(tool.contract.id)) throw new Error(`Tool already registered: ${tool.contract.id}`);
    this.#tools.set(tool.contract.id, tool);
    return this;
  }

  get(id: string): RegisteredTool | undefined { return this.#tools.get(id); }
  contracts(role?: AgentRole): ToolContract[] {
    return [...this.#tools.values()].map((tool) => tool.contract).filter((contract) => !role || contract.allowedAgentRoles.includes(role));
  }
}

export class ToolExecutor {
  readonly #completed = new Map<string, ToolExecutionResult>();
  readonly #requests = new Map<string, string>();
  readonly #pending = new Map<string, Promise<ToolExecutionResult>>();
  constructor(readonly registry: ToolRegistry) {}

  async execute(invocation: ToolInvocation, context: ToolExecutionContext): Promise<ToolExecutionResult> {
    const tool = this.registry.get(invocation.toolContractId);
    if (!tool) return { ok: false, status: "denied", error: `Unknown tool: ${invocation.toolContractId}`, changedDocumentIds: [] };
    const command = typeof invocation.arguments.command === "string" ? invocation.arguments.command.trim() : "";
    const virtualRead = tool.contract.id === "vfs.map:v1" && ["read", "search"].includes(String(invocation.arguments.action)) || tool.contract.id === "vfs.bash:v1" && (/^(ls|grep)(?:\s|$)/.test(command) || command === "postprocessing-complete");
    const contract = virtualRead ? { ...tool.contract, mutatesState: false, approval: "never" as const } : tool.contract;
    const validation = validateToolInvocation(contract, invocation);
    if (!validation.ok) return { ok: false, status: "denied", error: validation.errors.join("; "), changedDocumentIds: [] };
    if (invocation.runId !== context.runId || invocation.agentRole !== context.agentRole) {
      return { ok: false, status: "denied", error: "Tool invocation scope does not match the active run", changedDocumentIds: [] };
    }
    const approvalRequired = contract.approval === "always" || (contract.approval === "write" && contract.mutatesState);
    if (approvalRequired && !context.approved) return { ok: false, status: "denied", error: "Tool requires explicit approval", changedDocumentIds: [] };
    const replayKey = tool.contract.idempotent ? JSON.stringify([context.storyId, context.runId, context.agentRole, tool.contract.id, invocation.idempotencyKey]) : "";
    if (replayKey) {
      const request = JSON.stringify({ arguments: invocation.arguments, expectedRevision: invocation.expectedRevision ?? context.expectedRevision }, function (_key, value) {
        return value && typeof value === "object" && !Array.isArray(value) ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b))) : value;
      });
      if (this.#requests.has(replayKey) && this.#requests.get(replayKey) !== request) return { ok: false, status: "denied", error: "Idempotency key already belongs to a different request", changedDocumentIds: [] };
      this.#requests.set(replayKey, request);
    }
    if (replayKey && this.#completed.has(replayKey)) return structuredClone(this.#completed.get(replayKey)!);
    if (replayKey && this.#pending.has(replayKey)) return structuredClone(await this.#pending.get(replayKey)!);
    const execution = Promise.resolve().then(async (): Promise<ToolExecutionResult> => { try {
      const executionContext: ToolExecutionContext = {
        ...context,
        invocationId: invocation.id,
        ...(invocation.expectedRevision == null ? {} : { expectedRevision: invocation.expectedRevision }),
      };
      const result = await tool.execute(invocation.arguments, executionContext);
      if (replayKey && result.ok) this.#completed.set(replayKey, structuredClone(result));
      return result;
    } catch (error) {
      return { ok: false, status: "failed", error: String(error instanceof Error ? error.message : error), changedDocumentIds: [] };
    } });
    if (replayKey) this.#pending.set(replayKey, execution);
    try { return await execution; }
    finally { if (replayKey) this.#pending.delete(replayKey); }
  }
}

export interface MutableWorldAdapter {
  list(storyId: string): Promise<WorldDocument[]> | WorldDocument[];
  commit(transaction: WorldTransaction): Promise<{ documents: WorldDocument[]; changedDocumentIds: string[] }> | { documents: WorldDocument[]; changedDocumentIds: string[] };
}

export class MemoryWorldAdapter implements MutableWorldAdapter {
  #documents: WorldDocument[];
  constructor(documents: readonly WorldDocument[] = []) { this.#documents = structuredClone([...documents]); }
  list(storyId: string) { return structuredClone(this.#documents.filter((document) => document.storyId === storyId)); }
  commit(transaction: WorldTransaction) {
    const unrelated = this.#documents.filter((document) => document.storyId !== transaction.storyId);
    const result = applyWorldTransaction(this.#documents.filter((document) => document.storyId === transaction.storyId), transaction);
    this.#documents = [...unrelated, ...result.documents];
    return { documents: structuredClone(result.documents), changedDocumentIds: result.events.map((event) => event.documentId) };
  }
}

const valueString = (value: JsonValue | undefined) => typeof value === "string" ? value : "";
const valueNumber = (value: JsonValue | undefined) => Number.isFinite(Number(value)) ? Number(value) : undefined;
const forbiddenPathSegments = new Set(["__proto__", "prototype", "constructor"]);

function safePath(value: JsonValue | undefined): string[] {
  if (!Array.isArray(value) || !value.length || value.length > 12) throw new Error("patch path must contain 1..12 segments");
  const path = value.map((segment) => String(segment));
  if (path.some((segment) => !segment || segment.length > 96 || forbiddenPathSegments.has(segment))) throw new Error("patch path contains an unsafe segment");
  return path;
}

function patchFields(fields: JsonObject, operations: JsonValue | undefined): JsonObject {
  if (!Array.isArray(operations) || !operations.length || operations.length > 64) throw new Error("operations must contain 1..64 patches");
  const next = structuredClone(fields) as Record<string, JsonValue>;
  for (const raw of operations) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("patch operation must be an object");
    const operation = raw as Record<string, JsonValue>;
    const op = valueString(operation.op);
    if (!['set', 'remove', 'append'].includes(op)) throw new Error(`unsupported patch operation: ${op}`);
    const path = safePath(operation.path);
    let parent: Record<string, JsonValue> | JsonValue[] = next;
    for (let index = 0; index < path.length - 1; index++) {
      const segment = path[index]!;
      if (Array.isArray(parent) && (!/^\d+$/.test(segment) || Number(segment) >= parent.length)) throw new Error("Array patch index is out of bounds");
      const record = parent as Record<string, JsonValue>;
      const existing = record[segment];
      if (!existing || typeof existing !== "object") record[segment] = /^\d+$/.test(path[index + 1]!) ? [] : {};
      parent = record[segment] as Record<string, JsonValue> | JsonValue[];
    }
    const key = path.at(-1)!;
    if (Array.isArray(parent) && (!/^\d+$/.test(key) || Number(key) > parent.length || (op !== "set" && Number(key) >= parent.length))) throw new Error("Array patch index is out of bounds");
    const record = parent as Record<string, JsonValue>;
    if (op === "remove") { if (Array.isArray(parent)) parent.splice(Number(key), 1); else delete record[key]; }
    else if (op === "set") {
      if (!("value" in operation)) throw new Error("set patch requires value");
      record[key] = structuredClone(operation.value);
    } else {
      if (!("value" in operation)) throw new Error("append patch requires value");
      const existing = record[key];
      if (existing != null && !Array.isArray(existing)) throw new Error(`append target is not an array: ${path.join(".")}`);
      record[key] = [...(Array.isArray(existing) ? existing : []), structuredClone(operation.value)];
    }
  }
  return next;
}

export interface WorldToolRegistryOptions {
  onPlayerRoll?: (arguments_: JsonObject, context: ToolExecutionContext) => Promise<JsonValue>;
  onNpcRoll?: (arguments_: JsonObject, context: ToolExecutionContext) => Promise<JsonValue>;
  onUserQuestion?: (arguments_: JsonObject) => Promise<JsonValue>;
  fileTypes?: WorldFileTypeRegistry;
}

export function createWorldToolRegistry(adapter: MutableWorldAdapter, options: WorldToolRegistryOptions = {}): ToolRegistry {
  const registry = new ToolRegistry();
  const readers: AgentRole[] = ["main_gm", "creator_copilot", "player_ooc", "helper"];
  const writers: AgentRole[] = ["main_gm", "creator_copilot"];
  const assertAgentWritable = (document: WorldDocument, context: ToolExecutionContext) => {
    const definition = options.fileTypes?.resolveDocument(document);
    if (definition?.agentEditable === false) throw new Error(`Agent edits are disabled for File Type ${definition.id}`);
    const role = context.agentRole === "creator_copilot" ? "creator" : "gm";
    for (const permissions of [definition?.editableBy, document.editableBy]) {
      if (permissions && !permissions.includes(role)) throw new Error(`Agent cannot edit file ${document.id}`);
    }
  };
  // All world mutation tools, including VFS and multi-file helpers, use this gate.
  const commitAgentTransaction = async (transaction: WorldTransaction, context: ToolExecutionContext) => {
    const current = new Map((await adapter.list(context.storyId)).map(document => [document.id, document]));
    for (const mutation of transaction.mutations) {
      const previous = current.get(mutation.documentId);
      if(context.agentRole==='main_gm' && previous?.kind==='world_clock' && JSON.stringify(previous.fields.calendarDefinition)!==JSON.stringify(mutation.document?.fields.calendarDefinition)) throw new Error('Calendar definitions are creator-managed; use world.advanceClock to advance event time');
      if (previous) assertAgentWritable(previous, context);
      if (mutation.document) assertAgentWritable(mutation.document, context);
    }
    const projected = new Map(current);
    for (const mutation of transaction.mutations) {
      if (mutation.operation === "delete") projected.delete(mutation.documentId);
      else if (mutation.document) projected.set(mutation.documentId, mutation.document);
    }
    const projectedDocuments = [...projected.values()].filter(document => !document.deletedAt);
    if (options.fileTypes) {
      const types = options.fileTypes.list();
      const issueKey = (issue: ReturnType<typeof releaseReferenceIssues>[number]) => JSON.stringify(issue);
      const priorIssues = new Set(releaseReferenceIssues([...current.values()], types).map(issueKey));
      const introduced = releaseReferenceIssues(projectedDocuments, types).filter(issue => !priorIssues.has(issueKey(issue)));
      if (introduced.length) throw new Error(introduced.map(issue => `${issue.documentId}.${issue.path.join(".")}: ${issue.reason} (${issue.targetId})`).join("; "));
    }
    const locationIds = new Set(projectedDocuments.filter(document => isLocationFileType({ name: document.kind }, options.fileTypes?.resolveDocument(document))).map(document => document.id));
    const maps = projectedDocuments.filter(document => document.kind === "story_map").map(document => normalizeStoryMap(document.fields, context.storyId));
    for (const mutation of transaction.mutations) {
      if (mutation.document?.kind !== "story_map") continue;
      const map = normalizeStoryMap(mutation.document.fields, context.storyId);
      const errors = validateMapFileBindings(map, locationIds, maps);
      if (errors.length) throw new Error(errors.join("; "));
    }
    return adapter.commit(transaction);
  };
  const assertWritableFields = (document: WorldDocument, previous?: WorldDocument) => {
    const definition = options.fileTypes?.get(document.fileTypeId ?? "");
    for (const field of definition?.fields ?? []) {
      if (field.editableBy?.length === 1 && field.editableBy[0] === "system" && JSON.stringify(document.fields[field.id]) !== JSON.stringify(previous?.fields[field.id])) throw new Error(`Computed field ${field.id} is read-only; edit its source values instead`);
    }
  };
  const replaceDocument = async (document: WorldDocument, arguments_: JsonObject, context: ToolExecutionContext, reason: string, transactionKind: string, companions: WorldDocument[] = []): Promise<ToolExecutionResult> => {
    if (context.expectedRevision == null) return { ok: false, status: "denied", error: "expectedRevision is required", changedDocumentIds: [] };
    const previous = (await adapter.list(context.storyId)).find(item => item.id === document.id);
    assertWritableFields(document, previous);
    const canonicalDocument = options.fileTypes?.canonicalize(document) ?? document;
    // Moving/pinning a legacy or newly bootstrapped persona must not invent
    // gameplay fields just to satisfy a newer schema. Actual field edits and
    // type changes still require full validation.
    const unchangedFields = previous && previous.fileTypeId === canonicalDocument.fileTypeId && previous.kind === canonicalDocument.kind && JSON.stringify(previous.fields) === JSON.stringify(canonicalDocument.fields);
    const fileTypeValidation = unchangedFields ? undefined : options.fileTypes?.validate(canonicalDocument);
    if (fileTypeValidation && !fileTypeValidation.ok) return { ok: false, status: "failed", error: `File Type validation failed: ${fileTypeValidation.errors.join("; ")}`, changedDocumentIds: [] };
    const transaction: WorldTransaction = {
      id: `tool:${context.runId}:${context.invocationId ?? transactionKind}:${transactionKind}:${canonicalDocument.id}`, storyId: context.storyId,
      actor: context.agentRole === "creator_copilot" ? "creator" : "narrator", createdAt: context.now,
      mutations: [{ operation: "replace", documentId: canonicalDocument.id, expectedRevision: context.expectedRevision, document: canonicalDocument, reason, ...(Array.isArray(arguments_.sourceMessageIds) ? { sourceMessageIds: arguments_.sourceMessageIds.filter((value): value is string => typeof value === "string") } : {}) }],
    };
    for (const companion of companions) {
      const before = (await adapter.list(context.storyId)).find(item => item.id === companion.id);
      assertWritableFields(companion, before);
      const canonical = options.fileTypes?.canonicalize(companion) ?? companion;
      const validation = options.fileTypes?.validate(canonical);
      if (validation && !validation.ok) return {ok:false,status:'failed',error:`File Type validation failed: ${validation.errors.join('; ')}`,changedDocumentIds:[]};
      transaction.mutations.push({operation:'replace',documentId:canonical.id,expectedRevision:companion.revision,document:canonical,reason,sourceMessageIds:transaction.mutations[0]?.sourceMessageIds ?? []});
    }
    const committed = await commitAgentTransaction(transaction, context);
    return { ok: true, status: "completed", result: { transactionId: transaction.id, changedDocumentIds: committed.changedDocumentIds }, changedDocumentIds: committed.changedDocumentIds };
  };
  const exactDocument = async (fileId: string, context: ToolExecutionContext) => (await adapter.list(context.storyId)).find((document) => {
    if (document.id !== fileId || document.deletedAt) return false;
    if (document.visibility === "hidden" && context.agentRole !== "creator_copilot") return false;
    if (!document.editableBy?.length) return true;
    return context.agentRole === "creator_copilot" ? document.editableBy.some((role) => role === "creator" || role === "system") : document.editableBy.some((role) => role === "gm" || role === "system");
  });
  const readableDocument = async (fileId: string, context: ToolExecutionContext) => (await adapter.list(context.storyId)).find((document) => {
    if (document.id !== fileId || document.deletedAt) return false;
    return document.visibility !== "hidden" || context.agentRole === "creator_copilot";
  });
  registry.register({
    contract: { id: "world.read:v1", name: "world.read", version: 1, description: "Read exact world files. Prefer fieldPaths to inspect only missing fields; results are bounded.", risk: "read", allowedAgentRoles: readers, requiredArgumentKeys: ["fileIds"], allowedArgumentKeys: ["fileIds","fieldPaths","maxCharacters"], idempotent: false, mutatesState: false, approval: "never" },
    execute: async (arguments_, context) => {
      const fileIds = new Set(Array.isArray(arguments_.fileIds) ? arguments_.fileIds.filter((value): value is string => typeof value === "string") : []);
      if(!fileIds.size || fileIds.size>16) return {ok:false,status:'failed',error:'Read 1–16 exact files',changedDocumentIds:[]};
      const rawDocuments = await adapter.list(context.storyId);
      const allDocuments = context.agentRole === "player_ooc" ? playerVisibleDocuments(rawDocuments) : rawDocuments;
      const documents = allDocuments.filter((document) => fileIds.has(document.id) && (document.visibility !== "hidden" || context.agentRole === "creator_copilot"));
      const result = documents.map((document) => ({
        ...document,
        ...(context.agentRole === "creator_copilot" ? { coverage: structuredFieldCoverage(document.fields, options.fileTypes?.resolveDocument(document)?.fields ?? []) } : {}),
        ...(options.fileTypes?.get(document.fileTypeId || `core.${document.kind}`)?.sourceSchema && context.agentRole !== "player_ooc" ? { computed: projectComputedFields(document.fields, options.fileTypes.get(document.fileTypeId || `core.${document.kind}`)!.sourceSchema!, reference => {
          const id = typeof reference === "string" ? reference : reference && typeof reference === "object" ? String((reference as Record<string, unknown>).id ?? (reference as Record<string, unknown>).slug ?? "") : "";
          return allDocuments.find(item => !item.deletedAt && (item.id === id || item.fields.slug === id) && (item.visibility !== "hidden" || context.agentRole === "creator_copilot"))?.fields;
        }) } : {}),
        backlinks: computeBacklinks(allDocuments, document.id),
      }));
      const fieldPaths=Array.isArray(arguments_.fieldPaths)?arguments_.fieldPaths.map(String):[];
      const budget=Math.max(512,Math.min(48000,Number(arguments_.maxCharacters)||24000));
      const perFile=Math.floor((budget-2-Math.max(0,result.length-1))/Math.max(1,result.length));
      if(result.length && perFile<512) return {ok:false,status:'failed',error:'Read fewer files or allow at least 513 characters per file plus array framing',changedDocumentIds:[]};
      const projected=result.map(document=>projectWorldRead(document,fieldPaths,perFile));
      return { ok: true, status: "completed", result: projected, changedDocumentIds: [] };
    },
  });
  registry.register({
    contract: { id: "world.search:v1", name: "world.search", version: 1, description: "Search world files and return compact cited results", risk: "read", allowedAgentRoles: readers, requiredArgumentKeys: ["query"], allowedArgumentKeys: ["query", "limit"], idempotent: false, mutatesState: false, approval: "never" },
    execute: async (arguments_, context) => {
      const all = await adapter.list(context.storyId);
      const documents = context.agentRole === "player_ooc" ? playerVisibleDocuments(all) : all;
      const hits = retrieveWorldDocuments(documents, { query: valueString(arguments_.query), limit: valueNumber(arguments_.limit) ?? 12 });
      const byId = new Map(documents.map((document) => [document.id, document]));
      const result = hits.map((hit) => ({ id: hit.documentId, title: byId.get(hit.documentId)?.title ?? hit.documentId, summary: byId.get(hit.documentId)?.summary ?? "", score: hit.score, reasons: hit.reasons }));
      return { ok: true, status: "completed", result: result as unknown as JsonValue, changedDocumentIds: [] };
    },
  });
  registry.register({
    contract: { id: "world.list_file_types:v1", name: "world.listFileTypes", version: 1, description: "List compact File Type contracts; supply fileTypeId to read its full nested schema", risk: "read", allowedAgentRoles: readers, requiredArgumentKeys: [], allowedArgumentKeys: ["fileTypeId"], idempotent: false, mutatesState: false, approval: "never" },
    execute: (arguments_) => {
      const result = (options.fileTypes?.list() ?? []).filter(type => !arguments_.fileTypeId || type.id === arguments_.fileTypeId).map((fileType) => ({
        id: fileType.id,
        name: fileType.name,
        description: arguments_.fileTypeId ? fileType.description : fileType.description.slice(0, 240),
        designation: fileType.designation,
        category: (fileType as any).category ?? null,
        version: fileType.version,
        visibility: fileType.contextPolicy.visibility,
          editableBy: fileType.editableBy,
          agentEditable: fileType.agentEditable !== false,
        ...(arguments_.fileTypeId ? { fields: fileType.fields, sourceSchema: fileType.sourceSchema ?? null, revision: fileType.version } : { fieldCount: fileType.fields.length, requiredFields: fileType.fields.filter(field => field.required).map(field => field.id) }),
      }));
      return { ok: true, status: "completed", result: result as unknown as JsonValue, changedDocumentIds: [] };
    },
  });
  registry.register({
    contract: { id: "world.create_file_type:v1", name: "world.createFileType", version: 1, description: "Register a new dynamic File Type with custom schema fields and layout", risk: "write", allowedAgentRoles: ["creator_copilot"], requiredArgumentKeys: ["id", "name", "designation", "fields"], allowedArgumentKeys: ["id", "name", "description", "designation", "fields", "visibility", "layout", "reason"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      if (!options.fileTypes) return { ok: false, status: "denied", error: "No File Type registry is active", changedDocumentIds: [] };
      const id = valueString(arguments_.id);
      const name = valueString(arguments_.name);
      const designation = valueString(arguments_.designation) || id.replace(/^core\.|^custom\./, "");
      const description = valueString(arguments_.description);
      const visibility = valueString(arguments_.visibility) || "searchable";
      const rawFields = Array.isArray(arguments_.fields) ? arguments_.fields : [];
      const fieldIds = new Set<string>();
      for (const raw of rawFields) {
        const fieldId = raw && typeof raw === "object" && !Array.isArray(raw) ? valueString(raw.id).trim() : "";
        if (!fieldId) throw new Error("Each field requires a stable non-empty ID");
        if (fieldIds.has(fieldId)) throw new Error(`Duplicate field ID: ${fieldId}; reuse the existing field`);
        fieldIds.add(fieldId);
      }
      const fields = rawFields.map((field: any) => field.schema && typeof field.schema === "object" ? schemaField(valueString(field.id), field.schema as StructuredSchema, Boolean(field.required)) : ({
        ...structuredClone(field),
        id: valueString(field.id),
        label: valueString(field.label) || valueString(field.id),
        type: (valueString(field.type) || "text") as any,
        required: Boolean(field.required),
        ...(field.description ? { description: valueString(field.description) } : {}),
        ...(Array.isArray(field.options) ? { options: field.options.map(String) } : {}),
      }));
      const definition = {
        id,
        experienceId: context.storyId,
        version: 1,
        name,
        description,
        designation,
        contentType: "json" as const,
        sourceSchema: { type: "object", properties: Object.fromEntries(fields.map(field => [field.id, fieldDefinitionSchema(field)])), required: fields.filter(field => field.required).map(field => field.id) },
        fields,
        layout: Array.isArray(arguments_.layout) ? arguments_.layout as any : [],
        contextPolicy: { visibility: (visibility === "pinned" || visibility === "partial" ? visibility : "searchable") as any },
        displayVisibility: "player" as const,
        editableBy: ["creator" as const, "gm" as const, "system" as const],
        extensionIds: [],
        allowAdditionalFields: false,
        createdAt: context.now,
        updatedAt: context.now,
      };
      options.fileTypes.register(definition);
      return { ok: true, status: "completed", result: { fileTypeId: id, name, fieldCount: fields.length } as unknown as JsonValue, changedDocumentIds: [] };
    },
  });
  registry.register({
    contract: { id: "world.update_file_type:v1", name: "world.updateFileType", version: 1, description: "Update fields or layout for an existing File Type", risk: "write", allowedAgentRoles: ["creator_copilot"], requiredArgumentKeys: ["id", "patch"], allowedArgumentKeys: ["id", "patch", "reason"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      if (!options.fileTypes) return { ok: false, status: "denied", error: "No File Type registry is active", changedDocumentIds: [] };
      const id = valueString(arguments_.id);
      if (context.expectedRevision !== options.fileTypes.get(id)?.version) return { ok: false, status: "failed", error: "File Type revision changed; read its full schema and use its revision before updating", changedDocumentIds: [] };
      if (options.fileTypes.get(id)?.agentEditable === false) return { ok: false, status: "denied", error: "Agent edits are disabled for this File Type", changedDocumentIds: [] };
      const patch = arguments_.patch && typeof arguments_.patch === "object" && !Array.isArray(arguments_.patch) ? arguments_.patch : {};
      const allowed = new Set(["name", "description", "fields", "layout", "sourceSchema"]);
      if (Object.keys(patch).some(key => !allowed.has(key))) return { ok: false, status: "failed", error: "Patch supports name, description, fields, layout, sourceSchema only", changedDocumentIds: [] };
      const updated = options.fileTypes.updateFromAgent(id, patch as any);
      return { ok: true, status: "completed", result: { fileTypeId: id, version: updated.version } as unknown as JsonValue, changedDocumentIds: [] };
    },
  });
  registry.register({
    contract: { id: "world.create_file:v1", name: "world.createFile", version: 1, description: "Create one canonical file from a registered File Type; restrict displayVisibility for undiscovered or GM-only content while keeping context searchable", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["fileTypeId", "fileId", "title", "fields"], allowedArgumentKeys: ["fileTypeId", "fileId", "title", "summary", "fields", "references", "visibility", "displayVisibility", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      if (!options.fileTypes) return { ok: false, status: "denied", error: "No File Type registry is active", changedDocumentIds: [] };
      if (context.expectedRevision !== 0) return { ok: false, status: "denied", error: "expectedRevision must be zero for a new file", changedDocumentIds: [] };
      let rawFields = arguments_.fields;
      if (!rawFields || typeof rawFields !== "object" || Array.isArray(rawFields)) return { ok: false, status: "failed", error: "fields must be an object", changedDocumentIds: [] };
      if ("fields" in rawFields && rawFields.fields && typeof rawFields.fields === "object" && !Array.isArray(rawFields.fields)) {
        const { fields: nested, ...rest } = rawFields as Record<string, unknown>;
        rawFields = { ...(nested as Record<string, unknown>), ...rest } as JsonObject;
      }
      const rawReferences = Array.isArray(arguments_.references) ? arguments_.references : [];
      const references = rawReferences.map((value) => {
        if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("references must contain objects");
        const entry = value as Record<string, JsonValue>;
        const relation = valueString(entry.relation); const targetId = valueString(entry.targetId);
        if (!relation || !targetId) throw new Error("each reference needs relation and targetId");
        const metadata = entry.metadata && typeof entry.metadata === "object" && !Array.isArray(entry.metadata) ? entry.metadata as JsonObject : undefined;
        return { relation, targetId, ...(metadata ? { metadata } : {}) };
      });
      for (const reference of references) if (!await readableDocument(reference.targetId, context)) return { ok: false, status: "failed", error: `Reference target not found: ${reference.targetId}`, changedDocumentIds: [] };
      const fileTypeId = valueString(arguments_.fileTypeId);
      const definition = options.fileTypes.get(fileTypeId);
      if (!definition) return { ok: false, status: "failed", error: `Unknown File Type: ${fileTypeId}`, changedDocumentIds: [] };
      const role = context.agentRole === "creator_copilot" ? "creator" : "gm";
      if (definition.editableBy && !definition.editableBy.includes(role) && !definition.editableBy.includes("system")) {
        return { ok: false, status: "denied", error: `File Type ${fileTypeId} cannot be created or edited by ${context.agentRole} (allowed: ${definition.editableBy.join(", ")})`, changedDocumentIds: [] };
      }
      const visibility = valueString(arguments_.visibility);
      const displayVisibility = valueString(arguments_.displayVisibility);
      if (arguments_.displayVisibility != null && !["player", "gm", "creator", "hidden"].includes(displayVisibility)) throw new Error("Invalid displayVisibility");
      if (displayVisibility === "player" && definition.displayVisibility !== "player") throw new Error("A private File Type cannot be exposed to players by creating a file");
      const document = options.fileTypes.create({
        fileTypeId,
        fileId: valueString(arguments_.fileId),
        storyId: context.storyId,
        title: valueString(arguments_.title),
        summary: valueString(arguments_.summary),
        fields: rawFields as JsonObject,
        references,
        ...(visibility === "pinned" || visibility === "partial" || visibility === "searchable" || visibility === "hidden" ? { visibility } : {}),
        createdAt: context.now,
      });
      if (displayVisibility) document.displayVisibility = displayVisibility as NonNullable<WorldDocument["displayVisibility"]>;
      assertWritableFields(document);
      const transaction: WorldTransaction = {
        id: `tool:${context.runId}:${context.invocationId ?? "create-file"}:create-file:${document.id}`,
        storyId: context.storyId,
        actor: context.agentRole === "creator_copilot" ? "creator" : "narrator",
        createdAt: context.now,
        mutations: [{ operation: "create", documentId: document.id, expectedRevision: 0, document, reason: valueString(arguments_.reason) || `Create ${document.fileTypeId}`, ...(Array.isArray(arguments_.sourceMessageIds) ? { sourceMessageIds: arguments_.sourceMessageIds.filter((value): value is string => typeof value === "string") } : {}) }],
      };
      const committed = await commitAgentTransaction(transaction, context);
      return { ok: true, status: "completed", result: { transactionId: transaction.id, fileId: document.id, fileTypeId: document.fileTypeId ?? "", coverage: structuredFieldCoverage(document.fields, definition.fields), changedDocumentIds: committed.changedDocumentIds }, changedDocumentIds: committed.changedDocumentIds };
    },
  });
  const mutation = (operation: "create" | "replace" | "delete"): RegisteredTool => ({
    contract: { id: `world.${operation}:v1`, name: `world.${operation}`, version: 1, description: operation === "create" ? "Creator-only low-level import of a fully validated canonical file; gameplay must use world.createFile" : `${operation} one canonical world file`, risk: operation === "delete" ? "destructive" : "write", allowedAgentRoles: operation === "create" ? ["creator_copilot"] : writers, requiredArgumentKeys: operation === "delete" ? ["fileId"] : ["fileId", "document"], allowedArgumentKeys: ["fileId", "document", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: operation === "delete" ? "always" : "write" },
    execute: async (arguments_, context) => {
      const fileId = valueString(arguments_.fileId);
      const expectedRevision = context.expectedRevision;
      if (expectedRevision == null) return { ok: false, status: "denied", error: "expectedRevision is required", changedDocumentIds: [] };
      const suppliedDocument = operation === "delete" ? undefined : arguments_.document as unknown as WorldDocument;
      const document = suppliedDocument ? options.fileTypes?.canonicalize(suppliedDocument) ?? suppliedDocument : undefined;
      if (document) assertWritableFields(document, (await adapter.list(context.storyId)).find(item => item.id === document.id));
      const fileTypeValidation = document ? options.fileTypes?.validate(document) : undefined;
      if (fileTypeValidation && !fileTypeValidation.ok) return { ok: false, status: "failed", error: `File Type validation failed: ${fileTypeValidation.errors.join("; ")}`, changedDocumentIds: [] };
      const transaction: WorldTransaction = {
        id: `tool:${context.runId}:${context.invocationId ?? operation}:${operation}:${fileId}`,
        storyId: context.storyId,
        actor: context.agentRole === "creator_copilot" ? "creator" : "narrator",
        createdAt: context.now,
        mutations: [{ operation, documentId: fileId, expectedRevision, ...(operation === "delete" ? {} : { document: document! }), ...(typeof arguments_.reason === "string" ? { reason: arguments_.reason } : {}), ...(Array.isArray(arguments_.sourceMessageIds) ? { sourceMessageIds: arguments_.sourceMessageIds.filter((value): value is string => typeof value === "string") } : {}) }],
      };
      const committed = await commitAgentTransaction(transaction, context);
      return { ok: true, status: "completed", result: { transactionId: transaction.id, changedDocumentIds: committed.changedDocumentIds }, changedDocumentIds: committed.changedDocumentIds };
    },
  });
  registry.register(mutation("create")).register(mutation("replace")).register(mutation("delete"));
  registry.register({
    contract: { id: "world.patch:v1", name: "world.patch", version: 1, description: "Patch selected canonical fields without replacing unrelated data", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["fileId", "operations"], allowedArgumentKeys: ["fileId", "operations", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      const document = await exactDocument(valueString(arguments_.fileId), context);
      if (!document) return { ok: false, status: "failed", error: "File not found", changedDocumentIds: [] };
      return replaceDocument({ ...document, fields: patchFields(document.fields, arguments_.operations) }, arguments_, context, valueString(arguments_.reason) || "Patch canonical fields", "patch");
    },
  });
  registry.register({
    contract: { id: "world.link:v1", name: "world.link", version: 1, description: "Create or update one typed reference", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["sourceId", "relation", "targetId"], allowedArgumentKeys: ["sourceId", "relation", "targetId", "metadata", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      const sourceId = valueString(arguments_.sourceId); const relation = valueString(arguments_.relation); const targetId = valueString(arguments_.targetId);
      const [document, target] = await Promise.all([exactDocument(sourceId, context), readableDocument(targetId, context)]);
      if (!document || !relation || !targetId) return { ok: false, status: "failed", error: "Valid sourceId, relation and targetId are required", changedDocumentIds: [] };
      if (!target) return { ok: false, status: "failed", error: "Reference target does not exist or is not visible to this Agent", changedDocumentIds: [] };
      const metadata = arguments_.metadata && typeof arguments_.metadata === "object" && !Array.isArray(arguments_.metadata) ? arguments_.metadata as JsonObject : undefined;
      const references = [...document.references.filter((reference) => !(reference.relation === relation && reference.targetId === targetId)), { relation, targetId, ...(metadata ? { metadata } : {}) }];
      return replaceDocument({ ...document, references }, arguments_, context, valueString(arguments_.reason) || `Link ${relation} to ${targetId}`, "link");
    },
  });
  registry.register({
    contract: { id: "world.move:v1", name: "world.move", version: 1, description: "Confirm travel to a canonical Location; mapId/layerId changes the active main or submap before the turn header", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["entityId", "locationId"], allowedArgumentKeys: ["entityId", "locationId", "mapId", "layerId", "coordinates", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      const entityId = valueString(arguments_.entityId); const locationId = valueString(arguments_.locationId);
      const [document, location] = await Promise.all([exactDocument(entityId, context), readableDocument(locationId, context)]);
      if (!document || !location || location.kind !== "location") return { ok: false, status: "failed", error: "Entity or canonical Location not found", changedDocumentIds: [] };
      const coordinates = arguments_.coordinates && typeof arguments_.coordinates === "object" && !Array.isArray(arguments_.coordinates) ? arguments_.coordinates as JsonObject : undefined;
      if(coordinates && !['x','y'].every(key=>typeof coordinates[key]==='number'&&Number.isFinite(coordinates[key])&&Number(coordinates[key])>=0&&Number(coordinates[key])<=1)) throw new Error('Coordinates require finite x/y within 0..1');
      const mapId=valueString(arguments_.mapId),layerId=valueString(arguments_.layerId);
      let mapState:JsonObject={};
      if(mapId){const map=await readableDocument(mapId,context);if(map?.kind!=='story_map')throw new Error('Map Change requires a canonical map file');const definition=normalizeStoryMap(map.fields,context.storyId);const layer=definition.layers.find(item=>item.id===layerId)||(!layerId?definition.layers[0]:undefined);if(!layer)throw new Error('Map Change layer was not found');mapState={currentMapId:mapId,currentMapLayerId:layer.id};}
      else if(layerId) throw new Error('Map Change layer requires mapId');
      const references = [...document.references.filter((reference) => reference.relation !== "current_location"), { relation: "current_location", targetId: locationId }];
      const runtimeState: JsonObject = { ...(document.runtimeState ?? {}), currentLocationId: locationId,...mapState, ...(coordinates ? { coordinates } : {}) };
      const companions:WorldDocument[]=[];
      const activeMapId=valueString(runtimeState.currentMapId);
      const map=activeMapId?await readableDocument(activeMapId,context):undefined;
      if(map?.kind==='story_map') {
        const definition=normalizeStoryMap(map.fields,context.storyId);
        const targetLayer=valueString(runtimeState.currentMapLayerId);
        const anchors=definition.anchors.filter(anchor=>anchor.locationDocumentId===locationId&&(!targetLayer||anchor.layerId===targetLayer));
        const anchor=anchors.length===1?anchors[0]:undefined;
        const position=coordinates??anchor;
        const destinationLayer=targetLayer||anchor?.layerId;
        if(position&&destinationLayer&&Array.isArray(map.fields.tokens)) {
          let changed=false;
          const tokens=map.fields.tokens.map(raw=>{
            if(!raw||typeof raw!=='object'||Array.isArray(raw))return raw;
            const linked=raw.link===entityId||raw.id===entityId||(document.runtimeState?.isPlayer===true&&raw.slug==='player');
            if(!linked||raw.x===position.x&&raw.y===position.y&&raw.layerId===destinationLayer)return raw;
            changed=true;return {...raw,x:position.x!,y:position.y!,layerId:destinationLayer};
          });
          if(changed)companions.push({...map,fields:{...map.fields,tokens}});
        }
      }
      return replaceDocument({ ...document, references, runtimeState }, arguments_, context, valueString(arguments_.reason) || `Move to ${locationId}`, "move",companions);
    },
  });
  registry.register({
    contract: { id: "world.advance_clock:v1", name: "world.advanceClock", version: 1, description: "Advance story time from played events only. Supply durationSeconds (preferred) OR legacy durationMinutes, never both. Calendar rollover is calculated by the app.", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["fileId"], allowedArgumentKeys: ["fileId", "durationSeconds", "durationMinutes", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      const document = await exactDocument(valueString(arguments_.fileId), context);
      if (arguments_.durationSeconds != null && arguments_.durationMinutes != null) return { ok:false,status:'failed',error:'Supply only one duration unit',changedDocumentIds:[] };
      const seconds = arguments_.durationSeconds == null ? Number(arguments_.durationMinutes)*60 : Number(arguments_.durationSeconds);
      if (!document || document.kind !== "world_clock" || !Number.isSafeInteger(seconds) || seconds<0 || seconds>31_536_000_000) return {ok:false,status:'failed',error:'Valid clock and non-negative integer durationSeconds are required',changedDocumentIds:[]};
      const reading = readStoryCalendar({...document.fields,...document.runtimeState});
      if (reading) {
        const clockState = advanceStoryClock(reading.calendar,reading.state,seconds);
        return replaceDocument({...document,runtimeState:{...document.runtimeState,clockState:{...clockState},elapsedSeconds:clockState.elapsedSeconds,elapsedMinutes:Math.floor(clockState.elapsedSeconds/60)}},arguments_,context,valueString(arguments_.reason)||`Advance story clock ${seconds} seconds`,'advance-clock');
      }
      if (document.fields.calendarDefinition) return {ok:false,status:'failed',error:'Calendar definition or clock state is invalid; repair the file before advancing',changedDocumentIds:[]};
      const durationMinutes = seconds/60;
      const elapsedSeconds = Math.round(Math.max(0, valueNumber(document.runtimeState?.elapsedSeconds) ?? (valueNumber(document.runtimeState?.elapsedMinutes) ?? valueNumber(document.fields.elapsedMinutes) ?? 0) * 60)) + seconds;
      const elapsedMinutes = elapsedSeconds / 60;
      const currentTime = valueString(document.runtimeState?.currentTime) || valueString(document.fields.currentTime);
      const parsed = currentTime ? Date.parse(currentTime) : Number.NaN;
      if (!Number.isFinite(parsed)) return {ok:false,status:'failed',error:'Legacy clock has no valid date; configure its calendar first',changedDocumentIds:[]};
      const runtimeState: JsonObject = { ...(document.runtimeState ?? {}), elapsedSeconds, elapsedMinutes, currentTime: new Date(parsed + seconds * 1000).toISOString() };
      return replaceDocument({ ...document, runtimeState }, arguments_, context, valueString(arguments_.reason) || `Advance clock ${durationMinutes} minutes`, "advance-clock");
    },
  });
  registry.register({
    contract: { id: "world.commit_scene:v1", name: "world.commitScene", version: 1, description: "Commit a concise scene summary and consequences", risk: "write", allowedAgentRoles: writers, requiredArgumentKeys: ["fileId", "summary"], allowedArgumentKeys: ["fileId", "summary", "consequences", "reason", "sourceMessageIds"], idempotent: true, mutatesState: true, approval: "write" },
    execute: async (arguments_, context) => {
      const document = await exactDocument(valueString(arguments_.fileId), context); const summary = valueString(arguments_.summary).trim();
      if (!document || document.kind !== "scene" || !summary) return { ok: false, status: "failed", error: "Valid Scene and summary are required", changedDocumentIds: [] };
      const consequences = Array.isArray(arguments_.consequences) ? arguments_.consequences.filter((value): value is string => typeof value === "string").slice(0, 32) : [];
      const runtimeState: JsonObject = { ...(document.runtimeState ?? {}), status: "committed", committedAt: context.now, consequences };
      return replaceDocument({ ...document, summary, runtimeState }, arguments_, context, valueString(arguments_.reason) || "Commit completed scene", "commit-scene");
    },
  });

  // VFS: read files or directories by virtual path
  registry.register({
    contract: {
      id: "vfs.read:v1",
      name: "vfs.read",
      version: 1,
      description: "Read files or directory listings by virtual path (e.g. /Characters/Lorion.character.json or /Monsters/)",
      risk: "read",
      allowedAgentRoles: readers,
      requiredArgumentKeys: [],
      allowedArgumentKeys: ["paths", "path", "fieldPaths", "maxCharacters", "offset", "limit"],
      idempotent: false,
      mutatesState: false,
      approval: "never",
    },
    execute: async (arguments_, context) => {
      const all = await adapter.list(context.storyId);
      const documents = context.agentRole === "player_ooc" ? playerVisibleDocuments(all) : all.filter(document => document.visibility !== "hidden" || context.agentRole === "creator_copilot");
      const rawPaths: string[] = [];
      if (Array.isArray(arguments_.paths)) {
        for (const p of arguments_.paths) if (typeof p === "string") rawPaths.push(p);
      }
      if (typeof arguments_.path === "string") rawPaths.push(arguments_.path);
      if (!rawPaths.length) rawPaths.push("/");
      if(rawPaths.length>16 || rawPaths.some(path=>path.length>500)) throw new Error('Read at most 16 paths, each at most 500 characters');
      const budget=Number(arguments_.maxCharacters??24000);
      if(!Number.isFinite(budget)) throw new Error('Read budget must be finite');
      const perPath=Math.max(512,Math.floor(Math.min(48000,Math.max(1024,budget))/rawPaths.length)-550);
      const offset=Math.max(0,Math.trunc(Number(arguments_.offset??0))),limit=Math.max(1,Math.min(100,Math.trunc(Number(arguments_.limit??50))));
      if(!Number.isFinite(offset)||!Number.isFinite(limit)) throw new Error('Invalid directory page');
      const fieldPaths=Array.isArray(arguments_.fieldPaths)?arguments_.fieldPaths.filter((path):path is string=>typeof path==='string'):[];

      const results: Record<string, JsonValue> = {};
      for (const p of rawPaths) {
        if (p.endsWith("/") || p === "/") {
          const entries=vfsListDirectory(documents,p);
          results[p]=boundedReadPage(entries,offset,limit,perPath);
        } else {
          const doc = findDocumentByVfsPath(documents, p);
          if (doc) {
            const content=formatDocumentForVfs(doc,p).content;
            results[p]=fieldPaths.length || JSON.stringify(content).length>perPath ? projectWorldRead(doc,fieldPaths,perPath) : content as unknown as JsonValue;
          } else {
            results[p] = null;
          }
        }
      }
      return { ok: true, status: "completed", result: results as unknown as JsonValue, changedDocumentIds: [] };
    },
  });

  // VFS: write / create file
  registry.register({
    contract: {
      id: "vfs.write:v1",
      name: "vfs.write",
      version: 1,
      description: "Create or replace a file at a virtual path (e.g. /Characters/Lorion.character.json)",
      risk: "write",
      allowedAgentRoles: writers,
      requiredArgumentKeys: ["path", "content"],
      allowedArgumentKeys: ["path", "content", "reason", "sourceMessageIds", "expectedRevision"],
      idempotent: true,
      mutatesState: true,
      approval: "write",
    },
    execute: async (arguments_, context) => {
      const path = valueString(arguments_.path);
      const rawContent = arguments_.content;
      const documents = await adapter.list(context.storyId);
      const existing = findDocumentByVfsPath(documents, path);

      const expectedRevision = context.expectedRevision ?? (existing ? existing.revision : 0);
      const cleanTitle = path.split("/").pop()?.replace(/\.[^.]+\.(json|md)$/i, "").replace(/\.(json|md)$/i, "") || "untitled";
      const inferredKind = inferKindFromVfsPath(path);

      let fields: JsonObject = {};
      let summary = "";
      if (typeof rawContent === "string") {
        fields = { body: rawContent };
        summary = rawContent.slice(0, 300);
      } else if (rawContent && typeof rawContent === "object" && !Array.isArray(rawContent)) {
        fields = rawContent as JsonObject;
        summary = typeof fields.description === "string" ? fields.description : typeof fields.summary === "string" ? fields.summary : "";
      }

      const docId = existing?.id ?? `doc-${crypto.randomUUID()}`;
      const doc: WorldDocument = {
        id: docId,
        storyId: context.storyId,
        kind: inferredKind,
        schemaVersion: 1,
        revision: (existing?.revision ?? 0) + 1,
        title: typeof fields.name === "string" ? fields.name : (existing?.title ?? cleanTitle),
        summary: summary || existing?.summary || "",
        fields: { ...(existing?.fields ?? {}), ...fields },
        runtimeState: existing?.runtimeState ?? {},
        references: existing?.references ?? [],
        visibility: existing?.visibility ?? "partial",
        createdAt: existing?.createdAt ?? context.now,
        updatedAt: context.now,
      };

      const transaction: WorldTransaction = {
        id: `tool:${context.runId}:${context.invocationId ?? "vfs-write"}:vfs-write:${doc.id}`,
        storyId: context.storyId,
        actor: context.agentRole === "creator_copilot" ? "creator" : "narrator",
        createdAt: context.now,
        mutations: [
          {
            operation: existing ? "replace" : "create",
            documentId: doc.id,
            expectedRevision,
            document: doc,
            reason: valueString(arguments_.reason) || `Write ${path}`,
          },
        ],
      };
      const committed = await commitAgentTransaction(transaction, context);
      return {
        ok: true,
        status: "completed",
        result: { message: `Saved ${path}`, success: true, fileId: doc.id },
        changedDocumentIds: committed.changedDocumentIds,
      };
    },
  });

  // VFS: update file fields
  registry.register({
    contract: {
      id: "vfs.update:v1",
      name: "vfs.update",
      version: 1,
      description: "Patch fields on a virtual file (e.g. update character gold, hp, inventory, or campaign state)",
      risk: "write",
      allowedAgentRoles: writers,
      requiredArgumentKeys: ["path", "updates"],
      allowedArgumentKeys: ["path", "updates", "reason", "sourceMessageIds", "expectedRevision"],
      idempotent: true,
      mutatesState: true,
      approval: "write",
    },
    execute: async (arguments_, context) => {
      const path = valueString(arguments_.path);
      const updates = arguments_.updates as JsonObject;
      if (!updates || typeof updates !== "object" || Array.isArray(updates)) {
        return { ok: false, status: "failed", error: "updates must be an object", changedDocumentIds: [] };
      }
      const documents = await adapter.list(context.storyId);
      const existing = findDocumentByVfsPath(documents, path);
      if (!existing) {
        return { ok: false, status: "failed", error: `File not found at path: ${path}`, changedDocumentIds: [] };
      }

      const expectedRevision = context.expectedRevision ?? existing.revision;
      const newFields = { ...existing.fields };
      let newTitle = existing.title;
      let newSummary = existing.summary;
      const newRuntimeState = { ...(existing.runtimeState ?? {}) };

      for (const [key, val] of Object.entries(updates)) {
        if (key === "name" || key === "title") {
          if (typeof val === "string") newTitle = val;
        } else if (key === "summary" || key === "description") {
          if (typeof val === "string") newSummary = val;
        } else if (key === "runtimeState" && val && typeof val === "object" && !Array.isArray(val)) {
          Object.assign(newRuntimeState, val);
        } else {
          newFields[key] = val;
        }
      }

      const doc: WorldDocument = {
        ...existing,
        title: newTitle,
        summary: newSummary,
        fields: newFields,
        runtimeState: newRuntimeState,
        revision: existing.revision + 1,
        updatedAt: context.now,
      };

      const transaction: WorldTransaction = {
        id: `tool:${context.runId}:${context.invocationId ?? "vfs-update"}:vfs-update:${doc.id}`,
        storyId: context.storyId,
        actor: context.agentRole === "creator_copilot" ? "creator" : "narrator",
        createdAt: context.now,
        mutations: [
          {
            operation: "replace",
            documentId: doc.id,
            expectedRevision,
            document: doc,
            reason: valueString(arguments_.reason) || `Update ${path}`,
          },
        ],
      };
      const committed = await commitAgentTransaction(transaction, context);
      return {
        ok: true,
        status: "completed",
        result: { message: `Updated ${path}`, success: true, fileId: doc.id },
        changedDocumentIds: committed.changedDocumentIds,
      };
    },
  });

  // VFS: Scene Map Engine
  registry.register({
    contract: {
      id: "vfs.map:v1",
      name: "vfs.map",
      version: 1,
      description: "Maps require existing populated location files. Use create with locationDocumentId and a new mapId, then anchor to link a point to another location file and its map. Read before changes. Tokens are separate from navigable location anchors.",
      risk: "write",
      allowedAgentRoles: writers,
      requiredArgumentKeys: ["action"],
      allowedArgumentKeys: ["action", "mapId", "target", "query", "offset", "limit", "maxCharacters", "element", "position", "operations", "label", "slug", "link", "style", "reason", "sourceMessageIds", "expectedRevision", "locationDocumentId", "anchorId", "targetMapId", "layerId", "op"],
      idempotent: true,
      mutatesState: true,
      approval: "write",
    },
    execute: async (arguments_, context) => {
      const action = valueString(arguments_.action) || "read";
      const documents = await adapter.list(context.storyId);
      const requestedMapId=valueString(arguments_.mapId)||valueString(documents.find(d=>d.kind==='character'&&d.runtimeState?.isPlayer===true)?.runtimeState?.currentMapId);
      const mapDoc = requestedMapId?documents.find(d=>d.kind==='story_map'&&d.id===requestedMapId):documents.find((d) => d.kind === "story_map" || d.title === "Scene Map" || d.id === "scene-map");
      if(requestedMapId&&!mapDoc && action !== "create")throw new Error('Requested map file was not found');
      const currentMap = mapDoc ? normalizeStoryMap(mapDoc.fields, context.storyId) : createEmptyStoryMap(context.storyId, "scene-map");

      if (action === "read" || action === "search") {
        const offset=Number(arguments_.offset??0),limit=Number(arguments_.limit??50),budget=Number(arguments_.maxCharacters??24000);
        if(!Number.isFinite(budget))throw new Error('Read budget must be finite');
        if(action==='search') {
          const query=valueString(arguments_.query),page=boundedReadPage(searchStoryMap(currentMap,query),offset,limit,budget);
          return {ok:true,status:'completed',result:{action,query,matches:page.entries ?? [],count:page.total ?? 0,nextOffset:page.nextOffset ?? null},changedDocumentIds:[]};
        }
        const pageBudget=Math.max(512,Math.floor(Math.min(48000,Math.max(4096,budget))/4)-100);
        const pages=Object.fromEntries(Object.entries({layers:currentMap.layers.map(l=>({id:l.id,name:l.name,parentLayerId:l.parentLayerId??null})),tokens:currentMap.tokens??[],anchors:currentMap.anchors,areas:currentMap.areas??[]}).map(([key,items])=>[key,boundedReadPage(items,offset,limit,pageBudget)]));
        return {ok:true,status:'completed',result:{map:{name:currentMap.title,rootLocationId:currentMap.rootLocationId||null,fileId:mapDoc?.id??'scene-map',revision:mapDoc?.revision??0,...Object.fromEntries(Object.entries(pages).map(([key,page])=>[key,page.entries]))},pages:Object.fromEntries(Object.entries(pages).map(([key,page])=>[key,{total:page.total,nextOffset:page.nextOffset}]))} as JsonValue,changedDocumentIds:[]};
      }

      const locationId = valueString(arguments_.locationDocumentId) || valueString(arguments_.link);
      const location = documents.find(document => document.id === locationId && !document.deletedAt && isLocationFileType({ name: document.kind }, options.fileTypes?.resolveDocument(document)));
      if (action === "create") {
        if (mapDoc) throw new Error("Map already exists; read it before editing");
        if (!location) throw new Error("Create and read a real location file before creating its map");
        requireImageSubjectData(location);
      } else if (!mapDoc) throw new Error("Create a file-backed map before adding points");
      if (!["create", "anchor", "place", "move", "remove", "batch"].includes(action)) throw new Error(`Unsupported map action: ${action}`);
      if (["place", "move"].includes(action)) {
        const position = arguments_.position as { x?: unknown; y?: unknown } | undefined;
        if (!position || typeof position.x !== "number" || typeof position.y !== "number" || !Number.isFinite(position.x) || !Number.isFinite(position.y)) throw new Error("Map position requires finite x and y coordinates");
      }
      if (action === "batch" && (!Array.isArray(arguments_.operations) || !arguments_.operations.length)) throw new Error("Map batch requires at least one operation");
      let updatedMap = currentMap;
      if (action === "create" && location) updatedMap = { ...createEmptyStoryMap(context.storyId, requestedMapId || `map:${location.id}`, location.title), rootLocationId: location.id };
      if (action === "anchor") {
        const anchorId = valueString(arguments_.anchorId), existing = updatedMap.anchors.find(anchor => anchor.id === anchorId);
        if (arguments_.op === "remove") {
          if (!existing) throw new Error("Anchor was not found");
          updatedMap = { ...updatedMap, anchors: updatedMap.anchors.filter(anchor => anchor.id !== anchorId), routes: updatedMap.routes.filter(route => ![route.fromAnchorId, route.toAnchorId].includes(anchorId)) };
        } else {
          if (!location) throw new Error("Read and select an existing location file before linking a point");
          requireImageSubjectData(location);
          const destination = documents.filter(document => document.kind === "story_map").map(document => normalizeStoryMap(document.fields, context.storyId)).find(map => map.rootLocationId === location.id && (!arguments_.targetMapId || map.id === arguments_.targetMapId));
          if (!destination || destination.id === updatedMap.id) throw new Error("Create the selected location file's map before linking this point");
          const position = (arguments_.position || existing) as { x: number; y: number } | undefined;
          if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y) || position.x < 0 || position.x > 1 || position.y < 0 || position.y > 1) throw new Error("Anchor requires normalized coordinates");
          const layerId = valueString(arguments_.layerId) || existing?.layerId || updatedMap.layers[0]!.id;
          if (!updatedMap.layers.some(layer => layer.id === layerId)) throw new Error("Layer was not found");
          const anchor = { id: existing?.id || crypto.randomUUID(), label: valueString(arguments_.label) || existing?.label || location.title, x: position.x, y: position.y, layerId, visibility: existing?.visibility || "known" as const, locationDocumentId: location.id, targetMapId: destination.id };
          updatedMap = { ...updatedMap, anchors: [...updatedMap.anchors.filter(item => item.id !== anchor.id), anchor] };
        }
      }
      if (action === "place" && arguments_.position) {
        const pos = arguments_.position as { x: number; y: number };
        const label = valueString(arguments_.label) || "Token";
        const res = placeMapToken(updatedMap, {
          label,
          slug: valueString(arguments_.slug) || label.toLowerCase().replace(/\s+/g, "-"),
          x: pos.x,
          y: pos.y,
          link: valueString(arguments_.link),
          style: arguments_.style as { color?: string; icon?: string },
        });
        updatedMap = res.map;
      } else if (action === "move" && arguments_.position) {
        const pos = arguments_.position as { x: number; y: number };
        const element = arguments_.element as { slug?: string } | undefined;
        const slug = valueString(arguments_.slug) || element?.slug || valueString(arguments_.label);
        const res = moveMapToken(updatedMap, slug, pos);
        if (!res.token) throw new Error(`Map token not found: ${slug}`);
        updatedMap = res.map;
      } else if (action === "remove") {
        const slug = valueString(arguments_.slug) || valueString(arguments_.label);
        if (!(updatedMap.tokens ?? []).some(token => token.id === slug || token.slug.toLowerCase() === slug.toLowerCase() || token.label.toLowerCase() === slug.toLowerCase())) throw new Error(`Map token not found: ${slug}`);
        updatedMap = removeMapToken(updatedMap, slug);
      } else if (action === "batch" && Array.isArray(arguments_.operations)) {
        const batchRes = executeMapBatch(updatedMap, arguments_.operations as unknown as MapOperation[]);
        if (batchRes.results.length !== arguments_.operations.length || batchRes.results.some(result => result.status === "not_found")) throw new Error("Map batch contains invalid operations or missing tokens");
        updatedMap = batchRes.map;
      }

      const expectedRevision = context.expectedRevision ?? (mapDoc ? mapDoc.revision : 0);
      const savedDoc: WorldDocument = {
        ...mapDoc,
        id: mapDoc?.id ?? updatedMap.id,
        storyId: context.storyId,
        kind: "story_map",
        schemaVersion: 1,
        revision: (mapDoc?.revision ?? 0) + 1,
        title: mapDoc?.title ?? updatedMap.title,
        summary: mapDoc?.summary ?? "Interactive scene map with tokens and locations",
        fields: updatedMap as unknown as JsonObject,
        references: mapDoc?.references ?? [],
        visibility: mapDoc?.visibility ?? "pinned",
        createdAt: mapDoc?.createdAt ?? context.now,
        updatedAt: context.now,
      };

      const transaction: WorldTransaction = {
        id: `tool:${context.runId}:${context.invocationId ?? "map-sync"}:map-sync:${savedDoc.id}`,
        storyId: context.storyId,
        actor: context.agentRole === "creator_copilot" ? "creator" : "narrator",
        createdAt: context.now,
        mutations: [
          {
            operation: mapDoc ? "replace" : "create",
            documentId: savedDoc.id,
            expectedRevision,
            document: savedDoc,
            reason: `Map ${action}`,
            ...(Array.isArray(arguments_.sourceMessageIds)?{sourceMessageIds:arguments_.sourceMessageIds.filter((id):id is string=>typeof id==='string')}:{}),
          },
        ],
      };
      const committed = await commitAgentTransaction(transaction, context);
      return {
        ok: true,
        status: "completed",
        result: { status: "ok", action, mapRevision: updatedMap.revision } as unknown as JsonValue,
        changedDocumentIds: committed.changedDocumentIds,
      };
    },
  });

  // VFS: Bash Tool (ls, grep, scene pin, postprocessing-complete)
  registry.register({
    contract: {
      id: "vfs.bash:v1",
      name: "vfs.bash",
      version: 1,
      description: "Execute bash commands in the virtual environment (grep, ls, scene pin/unpin, postprocessing-complete)",
      risk: "write",
      allowedAgentRoles: writers,
      requiredArgumentKeys: ["command"],
      allowedArgumentKeys: ["command", "reason", "sourceMessageIds", "expectedRevision"],
      idempotent: true,
      mutatesState: true,
      approval: "write",
    },
    execute: async (arguments_, context): Promise<ToolExecutionResult> => {
      const command = valueString(arguments_.command).trim();
      const documents = await adapter.list(context.storyId);

      if (command === "postprocessing-complete") {
        return { ok: true, status: "completed", result: { output: "Postprocessing complete." }, changedDocumentIds: [] };
      }

      if (command.startsWith("ls")) {
        const parts = command.split(/\s+/);
        const targetDir = parts[1] || "/";
        const entries = vfsListDirectory(documents, targetDir);
        const output = entries.map((e) => (e.isDir ? `${e.name}/` : e.name)).join("\n");
        return { ok: true, status: "completed", result: { output }, changedDocumentIds: [] };
      }

      if (command.includes("grep")) {
        const queryMatch = command.match(/grep\s+["']?([^"']+)["']?\s*(.*)/i);
        const query = queryMatch?.[1] || "";
        const searchPath = queryMatch?.[2]?.trim() || "/";
        const grepResults = vfsGrep(documents, query, searchPath);
        const output = grepResults.map((r) => `${r.path}: ${r.matches.join(" | ")}`).join("\n");
        return { ok: true, status: "completed", result: { output: output || "No matches found" }, changedDocumentIds: [] };
      }

      if (command.startsWith("scene pin")) {
        const targetSlug = command.replace(/^scene pin\s*/i, "").trim();
        const doc = findDocumentByVfsPath(documents, targetSlug) ?? documents.find((item) => item.id === targetSlug || item.title.toLowerCase().replaceAll(" ", "-") === targetSlug.toLowerCase());
        if (!doc || doc.deletedAt) return { ok: false, status: "failed", error: "Pin target was not found", changedDocumentIds: [] };
        if (["gm", "creator", "hidden"].includes(doc.displayVisibility ?? "")) return { ok: false, status: "denied", error: "Private files cannot be pinned to the player sidebar", changedDocumentIds: [] };
        if (context.expectedRevision !== doc.revision) throw new Error("Pin target revision changed; read it again");
        const targets = [...documents.filter(item => item.id !== doc.id && item.runtimeState?.sidebarPinned === true), doc];
        const transaction: WorldTransaction = { id: `tool:${context.runId}:${context.invocationId}:scene-pin`, storyId: context.storyId, actor: context.agentRole === "creator_copilot" ? "creator" : "narrator", createdAt: context.now, mutations: targets.map(target => ({ operation: "replace", documentId: target.id, expectedRevision: target.revision, document: { ...target, runtimeState: { ...target.runtimeState, sidebarPinned: target.id === doc.id } }, reason: target.id === doc.id ? `Pin ${target.title}` : "Sidebar focus changed" })) };
        const committed = await commitAgentTransaction(transaction, context);
        return { ok: true, status: "completed", result: { pinnedFileId: doc.id, transactionId: transaction.id }, changedDocumentIds: committed.changedDocumentIds };
      }

      if (command === "scene unpin" || command.startsWith("scene unpin ")) {
        const targetSlug = command.slice("scene unpin".length).trim() || documents.find(item => item.runtimeState?.sidebarPinned === true)?.id || "";
        const doc = findDocumentByVfsPath(documents, targetSlug) ?? documents.find((item) => item.id === targetSlug || item.title.toLowerCase().replaceAll(" ", "-") === targetSlug.toLowerCase());
        if (!doc) return { ok: false, status: "failed", error: "Unpin requires an existing file id or path", changedDocumentIds: [] };
        return replaceDocument({ ...doc, runtimeState: { ...doc.runtimeState, sidebarPinned: false } }, arguments_, context, `Unpin ${doc.title}`, "scene_unpin");
      }

      return { ok: false, status: "failed", error: `Unsupported virtual command: ${command}`, changedDocumentIds: [] };
    },
  });

  registry.register({ contract: { id: "vfs.gm_roll:v1", name: "vfs.gmRoll", version: 1, description: "One roll command per NPC per turn; use labeled dice groups for multiple actions", risk: "read", allowedAgentRoles: ["main_gm"], requiredArgumentKeys: ["diceExpression"], allowedArgumentKeys: ["diceExpression", "reason", "targetCharacterSlug"], idempotent: true, mutatesState: false, approval: "never" }, execute: async (arguments_,context) => ({ ok: true, status: "completed", result: options.onNpcRoll ? await options.onNpcRoll(arguments_,context) : rollDice(valueString(arguments_.diceExpression)) as unknown as JsonValue, changedDocumentIds: [] }) });
  // VFS: Ask Player To Roll
  registry.register({
    contract: {
      id: "vfs.ask_roll:v1",
      name: "vfs.askRoll",
      version: 1,
      description: "Ask the player character to roll dice via interactive UI dialog",
      risk: "read",
      allowedAgentRoles: ["main_gm"],
      requiredArgumentKeys: ["diceExpression", "skillName"],
      allowedArgumentKeys: ["diceExpression", "skillName", "reason", "targetCharacterSlug"],
      idempotent: false,
      mutatesState: false,
      approval: "never",
    },
    execute: async (arguments_,context) => {
      if (options.onPlayerRoll) return { ok: true, status: "completed", result: await options.onPlayerRoll(arguments_,context), changedDocumentIds: [] };
      return {
        ok: false,
        status: "failed",
        error: "Player roll requires the durable interactive roll handler; no result is available",
        changedDocumentIds: [],
      };
    },
  });

  // VFS: Ask User Question
  registry.register({
    contract: {
      id: "vfs.ask_question:v1",
      name: "vfs.askQuestion",
      version: 1,
      description: "Prompt the user to choose between explicit options before continuing",
      risk: "read",
      allowedAgentRoles: ["creator_copilot", "main_gm"],
      requiredArgumentKeys: ["questions"],
      allowedArgumentKeys: ["questions"],
      idempotent: false,
      mutatesState: false,
      approval: "never",
    },
    execute: async (arguments_) => {
      if (options.onUserQuestion) return { ok: true, status: "completed", result: await options.onUserQuestion(arguments_), changedDocumentIds: [] };
      return {
        ok: true,
        status: "completed",
        result: {
          action: "askUserQuestion",
          questions: arguments_.questions ?? [],
        },
        changedDocumentIds: [],
      };
    },
  });

  return registry;
}
